// MIGRACION DEL SISTEMA DE EVENTOS — Horizon 4.0.0 elimino `CartAddEvent` (0 archivos lo usan
// en el vendor, contra 6 en 3.5.1) y lo reemplazo por `StandardEvents` de `@shopify/events`,
// que ya usan 18 archivos del tema. Nadie dispara `CartAddEvent` en el arbol mergeado, asi que
// dejarlo aca habria dejado el auto-open del drawer muerto en silencio.
//
// Se conserva `DialogComponent`: el markup de Esprit es `<dialog ref="dialog">`. El componente
// del vendor extiende `Component` y opera sobre `<theme-drawer>`, que no adoptamos (ruta A).
import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { StandardEvents } from '@shopify/events';
import { isMobileBreakpoint, requestIdleCallback } from '@theme/utilities';

/**
 * Importa bajo demanda los componentes de lineas y selector de cantidad del carrito, en vez de
 * cargarlos en todos los templates con un <script> sitewide. En /cart ya los carga scripts.liquid.
 *
 * Se dispara en idle apenas conecta el drawer (carga de pagina), no al abrirlo: eso lo saca de la
 * ruta critica de render sin dejar de garantizar que este listo antes de cualquier alta real.
 * `openDrawer()` y `open()` igual lo esperan como red de seguridad, porque el auto-open puede
 * ocurrir antes de que el idle callback haya corrido.
 * Cachea la promesa para que las llamadas repetidas no reimporten.
 * @returns {Promise<[unknown, unknown]> | void}
 */
let cartItemsScriptsPromise = null;

function ensureCartItemsScripts() {
  if (window.Theme?.template?.name === 'cart') return;
  if (cartItemsScriptsPromise) return cartItemsScriptsPromise;

  cartItemsScriptsPromise = Promise.all([
    import('@theme/component-cart-items'),
    import('@theme/component-cart-quantity-selector'),
  ]);
  return cartItemsScriptsPromise;
}

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /**
   * Ruta A: no se adopta <theme-drawer>, asi que se retira el getter `#themeDrawer` del vendor.
   * `#dialog` tampoco sirve como venia: hacia `closest('dialog')`, o sea buscaba un ANCESTRO,
   * y en el markup de Esprit el <dialog ref="dialog"> es descendiente de este componente.
   * @type {HTMLDialogElement | null}
   */
  get #dialog() {
    return /** @type {HTMLDialogElement | null} */ (this.refs.dialog ?? null);
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.addEventListener('click', this.#onTabClick);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }

    requestIdleCallback(() => ensureCartItemsScripts(), { timeout: 2000 });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.removeEventListener('click', this.#onTabClick);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  /** @param {MouseEvent} event */
  #onTabClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const tabButton = target.closest('.cart-drawer__tab');
    if (!(tabButton instanceof HTMLElement)) return;

    const tab = tabButton.dataset.tab;
    if (tab) this.#switchTab(tab);
  };

  /** @param {string} tab */
  #switchTab = (tab) => {
    const tabs = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('.cart-drawer__tab'));
    const contents = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('.cart-drawer__content'));

    tabs.forEach((btn) => {
      btn.classList.toggle('active', btn.dataset.tab === tab);
    });

    contents.forEach((content) => {
      const isActive = content.dataset.tabContent === tab;
      content.classList.toggle('active', isActive);
      content.style.display = isActive ? 'flex' : 'none';
    });
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Auto-abre el drawer al agregar al carrito y estabiliza el estado sticky.
   *
   * Portado de Horizon 4.0.0 y adaptado a `DialogComponent`: el vendor opera sobre
   * <theme-drawer>, que no adoptamos (ruta A), asi que sus `#themeDrawer.open()/close()`
   * se traducen a `showDialog()` / `closeDialog()`.
   *
   * Del vendor se adopta:
   *   - `event.action === 'add'`: abre solo en altas. Supera al `skipOpen` de Esprit, que era
   *     un flag puesto a mano al vaciar el carrito en component-cart-items.js.
   *   - `#isCartEmpty()`: no abrir un drawer vacio.
   *   - Diferimiento por `dialog:modal`: si el alta viene de quick-add, esperar a que ese modal
   *     cierre para que su restauracion de foco corra primero. Accesibilidad que Esprit no tenia.
   *   - `event.promise`: estabilizar el sticky recien cuando el update termino.
   *
   * De Esprit se conserva `#switchTab('cart')` antes de abrir: sin eso el drawer se abriria en
   * la pestana de wishlist si el usuario la habia dejado activa.
   *
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen =
      this.hasAttribute('auto-open') && event.action === 'add' && !this.#dialog?.open;

    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    const openDrawer = async () => {
      await ensureCartItemsScripts();
      this.#switchTab('cart');
      this.showDialog();
    };

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) openDrawer();

    event.promise
      ?.then(({ detail, cart }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        this.#announceCartCount(cart?.totalQuantity);

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#dialog?.open) openDrawer();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  /**
   * Anuncia el conteo del carrito a lectores de pantalla cuando el dialogo esta abierto.
   *
   * NOTA (D-005): `ref="liveRegion"` vive en <header-actions>, no dentro de este componente, asi
   * que `this.refs.liveRegion` no resuelve y el metodo retorna temprano. Era asi tambien antes
   * del upgrade (verificado contra a4d8eb8) y el anuncio efectivo lo hace header-actions.js.
   * Se conserva sin cambios: no altera comportamiento y vuelve a funcionar si algun dia el
   * liveRegion se mueve dentro del drawer.
   *
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  async open() {
    await ensureCartItemsScripts();
    this.#switchTab('cart');
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }


  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
